import React, { useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Paper,
  ButtonGroup,
  FormHelperText
} from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  Superscript,
  Subscript,
  YouTube as YouTubeIcon
} from '@mui/icons-material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  onBlur?: () => void;
  label?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  rows?: number;
  placeholder?: string;
}

// Common math symbols for middle school
const MATH_SYMBOLS = [
  { symbol: '°', label: 'Degree' },
  { symbol: '±', label: 'Plus/Minus' },
  { symbol: '×', label: 'Multiply' },
  { symbol: '÷', label: 'Divide' },
  { symbol: '√', label: 'Square Root' },
  { symbol: '²', label: 'Squared' },
  { symbol: '³', label: 'Cubed' },
  { symbol: '½', label: 'One Half' },
  { symbol: '¼', label: 'One Quarter' },
  { symbol: '¾', label: 'Three Quarters' },
  { symbol: '∞', label: 'Infinity' },
  { symbol: '≈', label: 'Approximately' },
  { symbol: '≤', label: 'Less Than or Equal' },
  { symbol: '≥', label: 'Greater Than or Equal' },
  { symbol: '≠', label: 'Not Equal' },
  { symbol: 'π', label: 'Pi' },
  { symbol: '∠', label: 'Angle' },
  { symbol: '△', label: 'Triangle' },
  { symbol: '⊥', label: 'Perpendicular' },
  { symbol: '∥', label: 'Parallel' }
];

/**
 * Presentational rich-text editor (Quill + math/formatting toolbar) driven by a
 * controlled `value`/`onChange`. Used directly for inline editing and wrapped by
 * FormRichTextField for react-hook-form usage so the editor UI lives in one place.
 */
const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  onBlur,
  label,
  required = false,
  helperText,
  error,
  rows = 4,
  placeholder = 'Enter problem statement...'
}) => {
  const quillRef = useRef<ReactQuill>(null);

  // Custom toolbar configuration
  const modules = {
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{ script: 'sub' }, { script: 'super' }],
        ['clean']
      ]
    }
  };

  const formats = ['bold', 'italic', 'underline', 'script'];

  // Insert symbol at cursor position
  const insertSymbol = useCallback((symbol: string) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      quill.insertText(range.index, symbol);
      quill.setSelection(range.index + symbol.length, 0);
    }
  }, []);

  // Format text functions
  const toggleFormat = useCallback((format: string) => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const currentFormat = quill.getFormat();
      quill.format(format, !currentFormat[format]);
    }
  }, []);

  // Insert superscript/subscript using Quill's native script format
  const insertScript = useCallback((type: 'sup' | 'sub') => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);

      if (range && range.length > 0) {
        const currentFormat = quill.getFormat(range.index, range.length);
        const scriptValue = type === 'sup' ? 'super' : 'sub';

        if (currentFormat.script === scriptValue) {
          quill.formatText(range.index, range.length, 'script', false);
        } else {
          quill.formatText(range.index, range.length, 'script', scriptValue);
        }
      } else {
        const placeholder = type === 'sup' ? 'superscript' : 'subscript';
        const scriptValue = type === 'sup' ? 'super' : 'sub';
        quill.insertText(range.index, placeholder, 'script', scriptValue);
        quill.setSelection(range.index, placeholder.length);
      }
    }
  }, []);

  // Insert YouTube URL
  const insertYouTubeUrl = useCallback(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      const youtubeUrl = '\n\nhttps://www.youtube.com/watch?v=VIDEO_ID\n\n';
      quill.insertText(range.index, youtubeUrl);
      quill.setSelection(range.index + youtubeUrl.length, 0);
    }
  }, []);

  const customStyle = {
    '& .ql-editor': {
      minHeight: `${rows * 1.5}em`,
      fontSize: '16px',
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
    },
    '& .ql-toolbar': {
      borderBottom: 'none',
      padding: '8px'
    },
    '& .ql-container': {
      borderTop: 'none'
    }
  };

  return (
    <Box>
      {label && (
        <Typography
          variant="body2"
          color={error ? 'error' : 'text.secondary'}
          sx={{ mb: 1 }}
        >
          {label}{required && ' *'}
        </Typography>
      )}

      {/* Custom toolbar with math symbols */}
      <Paper variant="outlined" sx={{ mb: 1 }}>
        <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
          {/* Formatting buttons */}
          <ButtonGroup size="small" sx={{ mb: 1 }}>
            <Tooltip title="Bold">
              <IconButton size="small" onClick={() => toggleFormat('bold')}>
                <FormatBold fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Italic">
              <IconButton size="small" onClick={() => toggleFormat('italic')}>
                <FormatItalic fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Underline">
              <IconButton size="small" onClick={() => toggleFormat('underline')}>
                <FormatUnderlined fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Superscript (select text first)">
              <IconButton size="small" onClick={() => insertScript('sup')}>
                <Superscript fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Subscript (select text first)">
              <IconButton size="small" onClick={() => insertScript('sub')}>
                <Subscript fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Insert YouTube Video URL">
              <IconButton size="small" onClick={insertYouTubeUrl}>
                <YouTubeIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ButtonGroup>

          {/* Math symbols */}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, mb: 0.5 }}>
            Math Symbols:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {MATH_SYMBOLS.map(({ symbol, label: symbolLabel }) => (
              <Tooltip key={symbol} title={symbolLabel}>
                <IconButton
                  size="small"
                  onClick={() => insertSymbol(symbol)}
                  sx={{
                    minWidth: '32px',
                    height: '32px',
                    fontSize: '16px',
                    border: '1px solid #e0e0e0',
                    '&:hover': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText'
                    }
                  }}
                >
                  {symbol}
                </IconButton>
              </Tooltip>
            ))}
          </Box>
        </Box>

        {/* Rich text editor */}
        <Box sx={customStyle}>
          <ReactQuill
            ref={quillRef}
            theme="snow"
            value={value || ''}
            onChange={onChange}
            onBlur={onBlur}
            modules={modules}
            formats={formats}
            placeholder={placeholder}
          />
        </Box>
      </Paper>

      {(error || helperText) && (
        <FormHelperText error={!!error}>
          {error || helperText}
        </FormHelperText>
      )}
    </Box>
  );
};

export default RichTextEditor;
